import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTableSort } from "@/lib/useTableSort";
import { SortableTh } from "@/components/SortableTh";
import { StatusFilter } from "@/components/StatusFilter";
import { usePersistedState } from "@/lib/usePersistedState";
import { ClientCombobox } from "@/components/ClientCombobox";
import { usFederalHolidays } from "@/lib/holidays";
import { QuickView, QVRow, qvRowClick } from "@/components/QuickView";
import { useColumnPrefs } from "@/lib/useColumnPrefs";
import { lastFirst } from "@/lib/names";
import { ColumnChooser } from "@/components/ColumnChooser";
import CompleteProjectModal from "@/components/CompleteProjectModal";

const empty = { client_id: "", name: "", po_number: "", subcontractor_number: "", description: "", location: "", default_pay_rate: 0, default_bill_rate: 0, default_per_diem: 0, default_ot_pay_rate: 0, default_ot_bill_rate: 0, ot_threshold_hours: 40, holiday_pay_rate: 0, holiday_bill_rate: 0, holiday_dates: [], supervisor_name: "", supervisor_email: "", supervisor_phone: "", supervisors: [], require_signature: false, sales_rep_id: null, status: "active" };

const supsOf = (p) => {
  const list = (p.supervisors || []).filter((s) => s.email);
  if (list.length) return list;
  return p.supervisor_email ? [{ name: p.supervisor_name || "", email: p.supervisor_email, phone: p.supervisor_phone || "" }] : [];
};

const PJ_COLUMNS = [
  { key: "client", label: "Client", locked: true },
  { key: "po_number", label: "PO #" },
  { key: "workers", label: "Worker assigned" },
  { key: "name", label: "Name", defaultHidden: true },
  { key: "location", label: "Location" },
  { key: "default_bill_rate", label: "Bill" },
  { key: "default_pay_rate", label: "Pay" },
  { key: "default_per_diem", label: "Per Diem" },
  { key: "status", label: "Status" },
];

export default function Projects() {
  const { user } = useAuth();
  const readOnly = user.role !== "admin";
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [otOn, setOtOn] = useState(false);
  const [holOn, setHolOn] = useState(false);
  const [err, setErr] = useState("");
  const [viewP, setViewP] = useState(null);
  const [holDate, setHolDate] = useState("");
  const [holYear, setHolYear] = useState(new Date().getFullYear());
  const [completeFor, setCompleteFor] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const load = () => Promise.all([api.get("/projects"), api.get("/clients"), api.get("/assignments"), api.get("/workers")])
    .then(([p, c, a, w]) => {
      setRows(Array.isArray(p.data) ? p.data : []); setClients(Array.isArray(c.data) ? c.data : []);
      setAssignments(Array.isArray(a.data) ? a.data : []); setWorkers(Array.isArray(w.data) ? w.data : []);
    });
  useEffect(() => { load(); }, []);
  // Active workers per project, "Last, First" and alphabetical, for the Worker assigned column.
  const workersByProject = useMemo(() => {
    const wname = (id) => workers.find((w) => w.id === id)?.name;
    const map = {};
    for (const a of assignments) {
      if (a.status !== "active") continue;
      const n = wname(a.worker_id);
      if (n) (map[a.project_id] ||= []).push(lastFirst(n));
    }
    for (const k of Object.keys(map)) map[k].sort((x, y) => x.localeCompare(y));
    return map;
  }, [assignments, workers]);
  const workersOf = (p) => workersByProject[p.id] || [];
  const salesReps = useMemo(() => workers.filter((w) => w.is_sales_rep).sort((a, b) => a.name.localeCompare(b.name)), [workers]);
  const repName = (id) => workers.find((w) => w.id === id)?.name || "";
  const save = async (e) => {
    e.preventDefault(); setErr("");
    // Blank supervisor rows are dropped; a name without an email is a mistake worth flagging.
    const incomplete = (form.supervisors || []).find((s) => ((s.name || "").trim() || (s.phone || "").trim()) && !(s.email || "").trim());
    if (incomplete) { setErr(`Supervisor "${(incomplete.name || "").trim() || "(no name)"}" needs an email address — or remove that row.`); return; }
    // Legacy single-supervisor fields are derived server-side from supervisors[0];
    // sending them as "" trips the email validator ("must have an @-sign").
    const { supervisor_name: _sn, supervisor_email: _se, supervisor_phone: _sp, supervisor_welcome_sent_at: _sw, ...base } = form;
    const payload = { ...base,
      default_pay_rate: parseFloat(form.default_pay_rate) || 0,
      default_bill_rate: parseFloat(form.default_bill_rate) || 0,
      default_per_diem: parseFloat(form.default_per_diem) || 0,
      default_ot_pay_rate: otOn ? (parseFloat(form.default_ot_pay_rate) || 0) : 0,
      default_ot_bill_rate: otOn ? (parseFloat(form.default_ot_bill_rate) || 0) : 0,
      ot_threshold_hours: otOn ? (parseFloat(form.ot_threshold_hours) || 40) : 40,
      holiday_pay_rate: holOn ? (parseFloat(form.holiday_pay_rate) || 0) : 0,
      holiday_bill_rate: holOn ? (parseFloat(form.holiday_bill_rate) || 0) : 0,
      holiday_dates: form.holiday_dates || [],
      supervisors: (form.supervisors || [])
        .filter((s) => (s.email || "").trim())
        .map((s) => ({ name: (s.name || "").trim(), email: s.email.trim(), phone: (s.phone || "").trim() || null })),
    };
    try {
      if (editingId) await api.put(`/projects/${editingId}`, payload);
      else await api.post("/projects", payload);
      const noSup = (payload.supervisors || []).length === 0 && !(payload.supervisor_email || "").trim();
      if (noSup && payload.status === "active") {
        toast.warning("Saved — but this project has no approving supervisor. Timesheets submitted for it can't be routed for approval until you add one.", { duration: 8000 });
      }
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const cname = (id) => clients.find((c) => c.id === id)?.name || "—";
  const hasApprover = (p) => ((p.supervisors || []).some((s) => s && s.email) || !!p.supervisor_email);
  const [statusFilter, setStatusFilter] = usePersistedState("sos-filter-projects", "active");
  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);
  const statusCounts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((r) => r.status === "active").length,
    completed: rows.filter((r) => r.status === "completed").length,
  }), [rows]);
  const { sorted, sortKey, sortDir, toggle } = useTableSort(filteredRows, {
    initialKey: "client",
    storageKey: "sos-sort-projects",
    getValue: (row, key) => (key === "client" ? cname(row.client_id) : key === "workers" ? workersOf(row).join(", ") : row[key]),
  });
  const { vis, hiddenSet, toggleColumn, resetColumns } = useColumnPrefs("projects", PJ_COLUMNS);

  const del = async (p) => {
    if (!window.confirm(`Delete project "${p.name}" permanently?\n\nThis cannot be undone. If it still has any assignments, timesheets, or is referenced by any invoice, the delete will be refused — set the project to Inactive instead.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success(`Deleted ${p.name}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div><div className="tinylabel">Master data</div><h1 className="text-3xl font-black">Projects</h1></div>
        {!readOnly && <button data-testid="new-project-btn" onClick={() => { setEditingId(null); setForm(empty); setOtOn(false); setHolOn(false); setOpen(true); }}
          className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2"><Plus size={18} /> New project</button>}
      </div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <StatusFilter
          value={statusFilter}
          onChange={setStatusFilter}
          counts={statusCounts}
          options={[
            { value: "active", label: "Active" },
            { value: "completed", label: "Completed" },
            { value: "all", label: "All" },
          ]}
        />
        <ColumnChooser columns={PJ_COLUMNS} hiddenSet={hiddenSet} onToggle={toggleColumn} onReset={resetColumns} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <SortableTh label="Client" colKey="client" activeKey={sortKey} direction={sortDir} onToggle={toggle} />
              {vis("po_number") && <SortableTh label="PO #" colKey="po_number" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              {vis("workers") && <SortableTh label="Worker assigned" colKey="workers" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              {vis("name") && <SortableTh label="Name" colKey="name" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              {vis("location") && <SortableTh label="Location" colKey="location" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              {vis("default_bill_rate") && <SortableTh label="Bill" colKey="default_bill_rate" activeKey={sortKey} direction={sortDir} onToggle={toggle} align="right" />}
              {vis("default_pay_rate") && <SortableTh label="Pay" colKey="default_pay_rate" activeKey={sortKey} direction={sortDir} onToggle={toggle} align="right" />}
              {vis("default_per_diem") && <SortableTh label="Per Diem" colKey="default_per_diem" activeKey={sortKey} direction={sortDir} onToggle={toggle} align="right" />}
              {vis("status") && <SortableTh label="Status" colKey="status" activeKey={sortKey} direction={sortDir} onToggle={toggle} />}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id} onClick={(e) => !readOnly && qvRowClick(e, () => setViewP(p))}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${!readOnly ? "cursor-pointer" : ""}`} data-testid={`project-row-${p.id}`}>
                <td className="px-4 py-3 font-medium" data-testid={`project-client-${p.id}`}>{cname(p.client_id)}
                  {p.status === "active" && !hasApprover(p) && (
                    <span data-testid={`no-approver-badge-${p.id}`}
                          className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold align-middle">
                      NO APPROVER
                    </span>
                  )}
                </td>
                {vis("po_number") && <td className="px-4 py-3 font-mono">{p.po_number}</td>}
                {vis("workers") && <td className="px-4 py-3" data-testid={`project-workers-${p.id}`}>
                  {workersOf(p).length === 0
                    ? <span className="text-slate-300">—</span>
                    : workersOf(p).map((n) => <div key={n} className="whitespace-nowrap">{n}</div>)}
                </td>}
                {vis("name") && <td className="px-4 py-3">{p.name}</td>}
                {vis("location") && <td className="px-4 py-3 text-slate-600">{p.location}</td>}
                {vis("default_bill_rate") && <td className="px-4 py-3 text-right font-mono">${p.default_bill_rate}</td>}
                {vis("default_pay_rate") && <td className="px-4 py-3 text-right font-mono">${p.default_pay_rate}</td>}
                {vis("default_per_diem") && <td className="px-4 py-3 text-right font-mono">${p.default_per_diem}</td>}
                {vis("status") && <td className="px-4 py-3"><StatusPill status={p.status} /></td>}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {!readOnly && (
                    <button data-testid={`welcome-supervisor-${p.id}`}
                            disabled={supsOf(p).length === 0}
                            onClick={async () => {
                              const sups = supsOf(p);
                              if (sups.length === 0) return;
                              const emails = sups.map((s) => s.email).join(", ");
                              const wasSent = p.supervisor_welcome_sent_at;
                              const msg = wasSent
                                ? `Resend the welcome email to ${emails}?\n\n(Last sent: ${new Date(wasSent).toLocaleString()})\n\nA copy is BCC'd to billing@safetyonsitesolutions.com.`
                                : `Send the welcome email to ${emails}?\n\nExplains what to expect, the 30-second approval flow, and how to keep our approval emails out of spam. A copy is BCC'd to billing@safetyonsitesolutions.com.\n\nTip: preview it first via Settings → Email previews.`;
                              if (!window.confirm(msg)) return;
                              try {
                                const { data } = await api.post(`/projects/${p.id}/welcome-supervisor`);
                                toast.success(`Welcome email sent to ${data.sent_to}`);
                                if (data.failed?.length) toast.error(`Failed: ${data.failed.join("; ")}`);
                                load();
                              } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
                            }}
                            title={
                              supsOf(p).length === 0
                                ? "Add a supervisor to this project first"
                                : (p.supervisor_welcome_sent_at
                                    ? `Welcome email sent ${new Date(p.supervisor_welcome_sent_at).toLocaleDateString()} — resend?`
                                    : `Send welcome email to ${supsOf(p).map((s) => s.email).join(", ")}`)
                            }
                            className={`text-xs font-semibold px-2 py-1 rounded border mr-2 inline-flex items-center gap-1 ${
                              supsOf(p).length === 0
                                ? "border-slate-200 text-slate-400 cursor-not-allowed opacity-60"
                                : p.supervisor_welcome_sent_at
                                    ? "border-slate-300 text-slate-600 hover:bg-slate-50"
                                    : "border-sky-200 text-sky-700 hover:bg-sky-50"
                            }`}>
                      <PaperPlaneTilt size={12} weight="bold" />
                      {p.supervisor_welcome_sent_at ? "Resend welcome" : "Send welcome"}
                    </button>
                  )}
                  {!readOnly && (
                    <button data-testid={`toggle-project-${p.id}`}
                            onClick={async () => {
                              if (p.status !== "completed") { setCompleteFor(p); return; }
                              if (!window.confirm(`Reactivate this project — "${p.name}"?`)) return;
                              try {
                                await api.put(`/projects/${p.id}`, { ...p, status: "active" });
                                toast.success(`${p.name} → active`);
                                load();
                              } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
                            }}
                            title={p.status === "completed" ? "Reactivate project" : "End all active workers and mark the project complete"}
                            className={`text-xs font-semibold px-2 py-1 rounded border mr-2 ${p.status === "completed" ? "border-sky-200 text-sky-700 hover:bg-sky-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                      {p.status === "completed" ? "Reactivate" : "End all & complete"}
                    </button>
                  )}
                  {!readOnly && <button data-testid={`edit-project-${p.id}`} onClick={() => { setEditingId(p.id); setForm({ ...empty, ...p, supervisors: supsOf(p) }); setOtOn(!!(p.default_ot_pay_rate || p.default_ot_bill_rate)); setHolOn(!!(p.holiday_pay_rate || p.holiday_bill_rate || (p.holiday_dates || []).length)); setOpen(true); }} className="text-green-600 hover:text-green-800 font-semibold">Edit</button>}
                  {!readOnly && (
                    <button data-testid={`delete-project-${p.id}`} onClick={() => del(p)}
                            title="Delete project"
                            className="ml-3 text-slate-400 hover:text-rose-600 align-middle">
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={save} className="bg-white rounded-md w-full max-w-2xl p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sticky -top-6 bg-white pt-1 pb-2 z-10"><h2 className="text-xl font-bold">{editingId ? "Edit project" : "New project"}</h2>
              <button type="button" data-testid="close-project-modal" onClick={() => setOpen(false)}><X size={20} /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Client</label>
                <ClientCombobox
                  value={form.client_id}
                  onChange={(v) => setForm({ ...form, client_id: v })}
                  clients={clients}
                  testId="project-input-client_id"
                  required
                />
              </div>
              {["name", "po_number", "subcontractor_number", "description", "location"].map((k) => (
                <div key={k}><label className="text-xs font-bold uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</label>
                  <input required={k === "name" || k === "po_number"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`project-input-${k}`} placeholder={k === "po_number" || k === "subcontractor_number" ? "TBD if unknown" : ""}
                    className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" /></div>
              ))}
              <div className="col-span-2 mt-1 pt-3 border-t border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="tinylabel mb-1">Supervisors (timesheet approvers)</div>
                    <p className="text-xs text-slate-500">Add every supervisor who may approve timesheets on this project (e.g. day-shift and night-shift). If there's more than one, workers pick the right approver when submitting their timesheet.</p>
                  </div>
                  <button type="button" data-testid="add-supervisor-btn"
                    onClick={() => setForm({ ...form, supervisors: [...(form.supervisors || []), { name: "", email: "", phone: "" }] })}
                    className="text-green-600 font-semibold flex items-center gap-1 text-sm shrink-0 ml-3"><Plus size={14} /> Add</button>
                </div>
                {(form.supervisors || []).length === 0 && (
                  <p data-testid="no-supervisors-note" className="text-xs text-amber-600 mt-2 font-semibold">No supervisors yet — add at least one so timesheets can be approved.</p>
                )}
                {(form.supervisors || []).map((s, i) => (
                  <div key={i} className="flex gap-2 items-center mt-2">
                    <input placeholder="Name" value={s.name || ""} data-testid={`supervisor-name-${i}`}
                      onChange={(e) => { const c = [...form.supervisors]; c[i] = { ...c[i], name: e.target.value }; setForm({ ...form, supervisors: c }); }}
                      className="flex-1 h-10 px-3 border border-slate-300 rounded-md" />
                    <input type="email" placeholder="Email (required)" value={s.email || ""} data-testid={`supervisor-email-${i}`}
                      onChange={(e) => { const c = [...form.supervisors]; c[i] = { ...c[i], email: e.target.value }; setForm({ ...form, supervisors: c }); }}
                      className="flex-1 h-10 px-3 border border-slate-300 rounded-md" />
                    <input placeholder="Phone (optional)" value={s.phone || ""} data-testid={`supervisor-phone-${i}`}
                      onChange={(e) => { const c = [...form.supervisors]; c[i] = { ...c[i], phone: e.target.value }; setForm({ ...form, supervisors: c }); }}
                      className="w-36 h-10 px-3 border border-slate-300 rounded-md" />
                    <button type="button" data-testid={`remove-supervisor-${i}`}
                      onClick={() => setForm({ ...form, supervisors: form.supervisors.filter((_, j) => j !== i) })}
                      className="p-2 text-red-600"><Trash size={16} /></button>
                  </div>
                ))}
                <label className="flex items-start gap-2 mt-3 cursor-pointer">
                  <input type="checkbox" checked={!!form.require_signature}
                    onChange={(e) => setForm({ ...form, require_signature: e.target.checked })}
                    data-testid="project-require-signature" className="h-4 w-4 mt-0.5 accent-green-600" />
                  <span className="text-sm text-slate-700">
                    Require supervisor <b>signature</b> to approve timesheets
                    <span className="block text-xs text-slate-500">
                      The approval page will show a sign-here box ("By signing below, you are confirming the
                      accuracy of this timesheet") and the Approve button stays locked until it's signed.
                      The signature is saved with the timesheet and appears on the supervisor's PDF copy.
                    </span>
                  </span>
                </label>
              </div>
              <div className="col-span-2 mt-1 pt-3 border-t border-slate-100">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Sales rep <span className="font-normal normal-case tracking-normal">(optional — leave blank for owner projects)</span></label>
                <select value={form.sales_rep_id || ""} onChange={(e) => setForm({ ...form, sales_rep_id: e.target.value || null })}
                        data-testid="project-sales-rep" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                  <option value="">— None (owner's project) —</option>
                  {salesReps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">Approved hours on this project count toward the rep's weekly break-even and commission.</p>
              </div>
              {["default_pay_rate", "default_bill_rate", "default_per_diem"].map((k) => (
                <div key={k}><label className="text-xs font-bold uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</label>
                  <input type="number" step="0.01" value={form[k] || 0} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`project-input-${k}`} className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" /></div>
              ))}
              <div className="col-span-2 mt-1 pt-3 border-t border-slate-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={otOn} data-testid="project-ot-checkbox"
                    onChange={(e) => {
                      setOtOn(e.target.checked);
                      if (e.target.checked && !parseFloat(form.ot_threshold_hours)) setForm((f) => ({ ...f, ot_threshold_hours: 40 }));
                    }}
                    className="h-4 w-4 accent-green-600" />
                  <span className="tinylabel">Overtime applies</span>
                </label>
                {!otOn && <p className="text-xs text-slate-400 mt-1">Check to set OT pay/bill rates and a weekly threshold for this project.</p>}
                {otOn && <p className="text-xs text-slate-500 mt-1">Hours beyond the weekly threshold bill/pay at these rates.</p>}
              </div>
              {otOn && (<>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">OT pay rate ($/hr)</label>
                <input type="number" step="0.01" value={form.default_ot_pay_rate || 0}
                  onChange={(e) => setForm({ ...form, default_ot_pay_rate: e.target.value })}
                  data-testid="project-input-default_ot_pay_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">OT bill rate ($/hr)</label>
                <input type="number" step="0.01" value={form.default_ot_bill_rate || 0}
                  onChange={(e) => setForm({ ...form, default_ot_bill_rate: e.target.value })}
                  data-testid="project-input-default_ot_bill_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">OT threshold (hrs / week)</label>
                <input type="number" step="0.5" value={form.ot_threshold_hours || 40}
                  onChange={(e) => setForm({ ...form, ot_threshold_hours: e.target.value })}
                  data-testid="project-input-ot_threshold_hours"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
                <div className="text-xs text-slate-400 mt-1">Default 40 (standard weekly OT threshold).</div>
              </div>
              </>)}
              <div className="col-span-2 mt-1 pt-3 border-t border-slate-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={holOn} data-testid="project-holiday-checkbox"
                    onChange={(e) => setHolOn(e.target.checked)}
                    className="h-4 w-4 accent-green-600" />
                  <span className="tinylabel">Holiday rates apply</span>
                </label>
                {!holOn && <p className="text-xs text-slate-400 mt-1">Check to set premium holiday pay/bill rates and designate holiday dates.</p>}
                {holOn && <p className="text-xs text-slate-500 mt-1">Hours worked on the dates below pay/bill at these premium rates. Holiday hours are carved out before the weekly OT threshold.</p>}
              </div>
              {holOn && (<>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Holiday pay rate ($/hr)</label>
                <input type="number" step="0.01" value={form.holiday_pay_rate || 0}
                  onChange={(e) => setForm({ ...form, holiday_pay_rate: e.target.value })}
                  data-testid="project-input-holiday_pay_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Holiday bill rate ($/hr)</label>
                <input type="number" step="0.01" value={form.holiday_bill_rate || 0}
                  onChange={(e) => setForm({ ...form, holiday_bill_rate: e.target.value })}
                  data-testid="project-input-holiday_bill_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Holiday dates</label>
                <div className="flex gap-2 mt-1">
                  <input type="date" value={holDate} onChange={(e) => setHolDate(e.target.value)}
                    data-testid="project-holiday-date-input"
                    className="h-11 px-3 border border-slate-300 rounded-md" />
                  <button type="button" data-testid="project-add-holiday-date"
                    onClick={() => {
                      if (!holDate) return;
                      const cur = form.holiday_dates || [];
                      if (!cur.includes(holDate)) setForm({ ...form, holiday_dates: [...cur, holDate].sort() });
                      setHolDate("");
                    }}
                    className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50">Add date</button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <select value={holYear} onChange={(e) => setHolYear(Number(e.target.value))}
                    data-testid="project-holiday-year-select"
                    className="h-9 px-2 border border-slate-300 rounded-md text-sm bg-white">
                    {[0, 1, 2].map((i) => {
                      const y = new Date().getFullYear() + i;
                      return <option key={y} value={y}>{y}</option>;
                    })}
                  </select>
                  <button type="button" data-testid="project-add-federal-holidays"
                    onClick={() => {
                      const cur = form.holiday_dates || [];
                      const merged = [...new Set([...cur, ...usFederalHolidays(holYear)])].sort();
                      const added = merged.length - cur.length;
                      setForm({ ...form, holiday_dates: merged });
                      toast.success(added > 0
                        ? `Added ${added} US federal holiday${added === 1 ? "" : "s"} for ${holYear}.`
                        : `All ${holYear} federal holidays were already on the list.`);
                    }}
                    className="h-9 px-3 border border-amber-300 bg-amber-50 text-amber-800 rounded-md text-sm font-semibold hover:bg-amber-100">
                    + Add all US federal holidays
                  </button>
                </div>
                <div className="text-xs text-slate-400 mt-1">Adds the 11 federal holidays (actual dates, not weekend-observed). Remove any this project doesn't observe.</div>
                {(form.holiday_dates || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="project-holiday-dates-list">
                    {(form.holiday_dates || []).map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold">
                        {d}
                        <button type="button" data-testid={`project-remove-holiday-${d}`}
                          onClick={() => setForm({ ...form, holiday_dates: (form.holiday_dates || []).filter((x) => x !== d) })}
                          className="text-amber-600 hover:text-rose-600 font-bold">×</button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-xs text-slate-400 mt-1">e.g. federal holidays this project observes. Timesheet hours on these dates automatically use the holiday rates.</div>
              </div>
              </>)}
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md">
                  <option value="active">Active</option>
                  <option value="proposed">Proposed</option>
                  <option value="completed">Completed</option>
                  <option value="not_awarded">Not awarded</option>
                  <option value="inactive">Inactive</option>
                </select></div>
            </div>
            {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="save-project-btn" className="h-11 px-4 bg-green-600 text-white font-semibold rounded-md">Save</button>
            </div>
          </form>
        </div>
      )}
      {viewP && (
        <QuickView title={viewP.name} subtitle={cname(viewP.client_id)} status={viewP.status}
                   onClose={() => setViewP(null)} testId="project-quick-view">
          <QVRow label="PO #" value={viewP.po_number} mono />
          <QVRow label="Sub #" value={viewP.subcontractor_number} mono />
          <QVRow label="Location" value={viewP.location} />
          <QVRow label="Description" value={viewP.description} />
          <QVRow label="Bill rate" value={`$${viewP.default_bill_rate}/hr`} mono />
          <QVRow label="Pay rate" value={`$${viewP.default_pay_rate}/hr`} mono />
          <QVRow label="Per diem" value={`$${viewP.default_per_diem}/day`} mono />
          {(viewP.default_ot_bill_rate > 0 || viewP.default_ot_pay_rate > 0) && (
            <>
              <QVRow label="OT bill / pay" value={`$${viewP.default_ot_bill_rate} / $${viewP.default_ot_pay_rate} per hr`} mono />
              <QVRow label="OT threshold" value={`${viewP.ot_threshold_hours} hrs/week`} mono />
            </>
          )}
          {supsOf(viewP).length === 0 && <QVRow label="Supervisors" value="—" />}
          {supsOf(viewP).map((s, i) => (
            <QVRow key={i} label={supsOf(viewP).length > 1 ? `Supervisor ${i + 1}` : "Supervisor"}
                   value={`${s.name || "—"} · ${s.email}${s.phone ? ` · ${s.phone}` : ""}`} />
          ))}
          <QVRow label="Signature required" value={viewP.require_signature ? "Yes — supervisors sign to approve" : "No"} />
          <QVRow label="Sales rep" value={viewP.sales_rep_id ? repName(viewP.sales_rep_id) : "— (owner project)"} />
          <QVRow label="Welcome email" value={viewP.supervisor_welcome_sent_at ? `Sent ${new Date(viewP.supervisor_welcome_sent_at).toLocaleDateString()}` : "Not sent"} />
        </QuickView>
      )}
      {completeFor && <CompleteProjectModal project={completeFor} onClose={() => setCompleteFor(null)} onDone={() => { setCompleteFor(null); load(); }} />}
    </div>
  );
}
